
import type { ApiError, Identifiable } from '@empathyco/playboard-core-domain'
import type { FeedbackMessages, TaggingActionPayload } from '../../models'
import type { StorageService } from '../../services/interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { EngagementActionsTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-actions-type.enum'

import { EngagementTypeComponentEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-type-component.enum'
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from 'vue'
import { useApiError } from '../../composables/use-api-error'
import { mapActions } from '../../composables/utils'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ColorEnum, FeatureIdEnum } from '../../enums'
import { APP_CONSTS } from '../../store/app/app.consts'
import { TAGGING_CONSTS } from '../../store/tagging/tagging.consts'
import { TaggingUtils } from '../../utils/tagging.utils'

import { UiCheckbox, UiModal, UiTextbox } from '../keen-ui'
import { EpbBadge } from '../shared/epb-badge'
import { EpbButton } from '../shared/epb-button'
import { EpbText } from '../shared/epb-text'

interface Question extends Identifiable<number> {
  message: string
  schedule?: {
    from?: Date
    to?: Date
  }
}

export default defineComponent({
  name: 'FeedbackModal',
  components: {
    EpbBadge,
    EpbButton,
    EpbText,
    UiCheckbox,
    UiModal,
    UiTextbox,
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const storageService = CONTAINER.get<StorageService>(TYPES.StorageService)

    const { tagAction } = mapActions(ctx.proxy.$store, TAGGING_CONSTS.moduleName, {
      tagAction: TAGGING_CONSTS.actions.tagAction,
    })

    const { submitFeedback } = mapActions(ctx.proxy.$store, TAGGING_CONSTS.moduleName, {
      submitFeedback: TAGGING_CONSTS.actions.submitFeedback,
    })

    const { getErrorMessage } = useApiError()

    const messages = ctx.proxy.$epb.messages.feedback as FeedbackMessages

    const baseClass = 'epb-feedback-modal'

    const questions: Array<Question> = [
      {
        id: 1,
        message: messages.questions[1],
      },
    ]

    const feelings = ['happy', 'neutral', 'sad']

    const modal = ref(null)

    const selectedFeeling = ref('')
    const feedbackText = ref('')
    const dontShowAgain = ref(false)
    const submitted = ref(false)

    const currentQuestion = computed<Question | undefined>(() => {
      let result = questions[questions.length - 1]
      const now = new Date()
      if (result.schedule?.from && result.schedule.from.getTime() > now.getTime()) {
        result = undefined
      } else if (result.schedule?.to && result.schedule.to.getTime() < now.getTime()) {
        result = undefined
      } else if (getStorageInfo().hasOwnProperty(result.id)) {
        result = undefined
      }
      return result
    })

    const changeSelectedFeeling = (feeling: string): void => {
      selectedFeeling.value = selectedFeeling.value === feeling ? '' : feeling
    }

    function tagCloseModal(): void {
      const taggingPayload: TaggingActionPayload = {
        ...TaggingUtils.getTaggingPayload(FeatureIdEnum.FEEDBACK, DomainEnum.GLOBAL),
        type: EngagementActionsTypeEnum.CLICK,
        component: dontShowAgain.value
          ? EngagementTypeComponentEnum.CLOSE_DONT_SHOW_AGAIN
          : EngagementTypeComponentEnum.CLOSE,
      }
      tagAction(taggingPayload)
    }

    function onClose(): void {
      if (submitted.value) {
        storageUserAction('submitted')
      } else if (dontShowAgain.value) {
        storageUserAction('skipped')
      }
      if (!submitted.value) {
        tagCloseModal()
      }
    }

    function storageUserAction(action: 'submitted' | 'skipped'): void {
      storageService.setItem(
        APP_CONSTS.storage.feedback,
        JSON.stringify({
          ...getStorageInfo(),
          [currentQuestion.value.id]: action,
        }),
      )
    }

    function getStorageInfo(): Record<number, string> {
      return JSON.parse(storageService.getItem(APP_CONSTS.storage.feedback) ?? '{}')
    }

    async function submit(): Promise<void> {
      let score: number
      switch (selectedFeeling.value) {
        case 'happy':
          score = 100
          break
        case 'neutral':
          score = 50
          break
        case 'sad':
          score = 0
          break
      }
      try {
        emit('updateLoading', { visible: true })
        await submitFeedback({
          questionId: currentQuestion.value.id,
          question: currentQuestion.value.message,
          score,
          comment: feedbackText.value,
        })
        submitted.value = true
        modal.value.close()
        emit('showSnackbar', messages.success)
      } catch (error) {
        emit('showError', getErrorMessage(error as ApiError))
      } finally {
        emit('updateLoading', { visible: false })
      }
    }

    onMounted(() => {
      modal.value?.open()
    })

    return {
      messages,
      baseClass,
      feelings,
      modal,
      selectedFeeling,
      feedbackText,
      dontShowAgain,
      currentQuestion,
      changeSelectedFeeling,
      onClose,
      submit,
      ColorEnum,
    }
  },
})
